import { ReactElement, useEffect, memo, MouseEventHandler } from 'react';
import { styled, Avatar, Box, Text } from '@cad-arc';
import { MapMode } from '../types';
import { Basemap, MapView } from '../constructors';
import { ESRI_ALTERNATIVE_BASEMAPS, ESRI_BASEMAPS } from '../configurations';
import { useCurrentMapModeImage } from '../hooks/use-current-map-mode-image';
import { logMapError } from '../helpers/utils';

//TODO: move colors to css
import { colors } from '~/colors/colors';

const MapModeToggleButton = styled('button')`
    position: absolute;
    bottom: 20px;
    left: 12px;
    border: none;
    background: none;
    margin: none;
    padding: none;
`;

type EsriToggleMapModeButtonProps = {
    theme: 'dark' | 'light';
    mapMode: MapMode;
    setMapMode: (newMapMode: MapMode) => void;
    mapView: MapView | undefined;
    embedded?: boolean;
    displayOnlyMode?: boolean;
    isAlternativeBasemap?: boolean;
    setMapLoadFailed?: (failed: boolean) => void;
    isFirstResponder?: boolean;
};

export const EsriToggleMapModeButton = memo(function EsriToggleMapModeButton({
    theme,
    mapMode,
    setMapMode,
    mapView,
    embedded,
    displayOnlyMode,
    isAlternativeBasemap,
    setMapLoadFailed,
    isFirstResponder,
}: EsriToggleMapModeButtonProps): ReactElement | null {
    const currentMapModeImage = useCurrentMapModeImage(mapMode, isAlternativeBasemap);

    const defaultMode = theme === 'dark' ? 'DARK_MODE' : 'LIGHT_MODE';
    const defaultModeAlt = theme === 'dark' ? 'DARK_HUMAN_MODE' : 'LIGHT_HUMAN_MODE';

    const isDefaultMode = mapMode === defaultMode;
    const isDefaultModeAlt = mapMode === defaultModeAlt;

    useEffect(() => {
        const chosenBasemapConfiguration = Object.values(
            isAlternativeBasemap ? ESRI_ALTERNATIVE_BASEMAPS : ESRI_BASEMAPS
        ).find((basemapInfo) => basemapInfo.mode === mapMode);

        if (chosenBasemapConfiguration?.id && mapView) {
            try {
                const basemap = chosenBasemapConfiguration?.isPortalItem
                    ? new Basemap({
                          portalItem: {
                              id: chosenBasemapConfiguration.id,
                          },
                      })
                    : new Basemap({
                          style: {
                              id: chosenBasemapConfiguration.id,
                          },
                      });

                basemap
                    .load()
                    .then(() => {
                        mapView.map.basemap = basemap;
                    })
                    .catch((error) => {
                        setMapLoadFailed?.(true);
                        const errorMessage = error?.message ?? '';
                        if (errorMessage.toLowerCase().includes('token')) {
                            logMapError({
                                source: 'service',
                                event: 'token_invalid',
                                error,
                                extraData: {
                                    chosenBasemapConfiguration,
                                    isAlternativeBasemap,
                                },
                            });
                        } else {
                            logMapError({
                                source: 'service',
                                event: 'basemap_toggle_load_error',
                                error,
                                extraData: {
                                    chosenBasemapConfiguration,
                                    isAlternativeBasemap,
                                },
                            });
                        }
                    });
            } catch (error) {
                setMapLoadFailed?.(true);
                logMapError({
                    source: 'service',
                    event: 'basemap_toggle_error',
                    error: error as Error,
                    extraData: {
                        chosenBasemapConfiguration,
                        isAlternativeBasemap,
                    },
                });
            }
        }
    }, [mapMode, mapView, isAlternativeBasemap, setMapLoadFailed, isFirstResponder]);

    // TODO: RND-33840 Shift+Click toggles between regular and human geography maps (test feature)
    //       Remove before deploy.
    const handleMapModeToggleButtonClick: MouseEventHandler<HTMLButtonElement> = (event) => {
        if (isDefaultMode && event.shiftKey) {
            setMapMode(defaultModeAlt);
        } else if (isDefaultModeAlt && event.shiftKey) {
            setMapMode(defaultMode);
        } else {
            setMapMode(isDefaultMode || isDefaultModeAlt ? 'SATELLITE_MODE' : defaultMode);
        }
    };

    return embedded || displayOnlyMode || !mapView ? null : (
        <MapModeToggleButton
            onClick={handleMapModeToggleButtonClick}
            aria-label={`Switch to ${isDefaultMode || isDefaultModeAlt ? 'Satellite' : 'Default'} mode`}
            data-testid="MAP_MODE_TOGGLE_BUTTON"
        >
            <Avatar aria-hidden name=" " src={currentMapModeImage} size="lg">
                <Box
                    height="100%"
                    width="100%"
                    position="absolute"
                    borderRadius="0px 0px 3px 3px"
                    background={`linear-gradient(180deg, ${colors.mapModeButtonGradientStart} 0%, ${colors.mapModeButtonGradientEnd} 100%)`}
                >
                    <Text
                        fontWeight="bold"
                        fontSize={14}
                        align="center"
                        casing="capitalize"
                        style={{
                            position: 'absolute',
                            bottom: '5px',
                            width: '100%',
                            color: colors.mapModeButtonDefaultText,
                        }}
                    >
                        {isDefaultMode || isDefaultModeAlt ? 'Satellite' : 'Default'}
                    </Text>
                </Box>
            </Avatar>
        </MapModeToggleButton>
    );
});
